import type { ClinicalAssessmentEntry, PrescribedMedicine } from '@/ayurveda/types/ayurveda';

export interface TreatmentRowsProps {
  entries: ClinicalAssessmentEntry[];
}

function Row({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex flex-col gap-3 rounded-2xl bg-[#F8F8F1] px-4 py-1">
      <span className="font-hanken text-base font-medium text-[#42434B]">{label}:</span>
      <p
        className={
          value
            ? 'whitespace-pre-wrap text-sm text-[#585859]'
            : 'text-sm italic text-muted-foreground'
        }
      >
        {value || 'No data available'}
      </p>
    </div>
  );
}

/** Medicines / Treatment Principle / Treatment Given rows (no outer card — used inside AITranscribedDataCard). */
export function TreatmentRows({ entries }: TreatmentRowsProps) {
  const medicines = entries.find((e) => e.parameter === 'treatment_medicines')?.value;
  const principle = entries.find((e) => e.parameter === 'treatment_principle')?.value;
  const given = entries.find((e) => e.parameter === 'treatment_given')?.value;

  return (
    <div className="flex flex-col gap-2">
      <Row label="Medicines" value={medicines} />
      <Row label="Treatment Principle" value={principle} />
      <Row label="Treatment Given" value={given} />
    </div>
  );
}

const MEDICINE_COLUMNS: Array<{ key: keyof PrescribedMedicine; label: string }> = [
  { key: 'name', label: 'Medicine Name' },
  { key: 'form', label: 'Form' },
  { key: 'dose', label: 'Dosage' },
  { key: 'frequency', label: 'Frequency' },
  { key: 'duration', label: 'Duration' },
  { key: 'anupana', label: 'Anupana' },
  { key: 'timing', label: 'Timing' },
  { key: 'additional_info', label: 'Additional Info' },
];

/**
 * Structured medicines table — Case Sheet only (not shown on the live
 * consultation page). A single horizontal table, one row per medicine —
 * same layout convention as the "Medication" (prescribedMedications) table
 * in CaseSheetView.tsx, just with the Ayurveda-specific columns.
 */
export function MedicinesTable({ medicines }: { medicines: PrescribedMedicine[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[760px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-gray-200 text-left">
            {MEDICINE_COLUMNS.map(({ key, label }) => (
              <th
                key={key}
                className="whitespace-nowrap px-3 py-2.5 align-middle font-hanken font-medium text-[#051858]"
              >
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {medicines.map((med, idx) => (
            <tr key={idx} className="border-b border-gray-100">
              {MEDICINE_COLUMNS.map(({ key }) => (
                <td
                  key={key}
                  className="break-words px-3 py-2.5 align-top font-hanken text-[#585859]"
                >
                  {med[key] || '—'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
